/*!
 * EGT — THE CAPTURE DOOR (WEB-2 / WEB-3 / STR-3).
 *
 * One reusable inline "Be first to know" email capture, plus the ONE definitive
 * source→newsletter map that both the surfaces and the server route read. Any
 * surface can drop the capture in; every capture self-segments by its `source`.
 *
 * WHY THIS FILE EXISTS AT ALL: the old capture on the home footer and on /voice
 * was a `<form action="/contact-us" method="get">` — it navigated the visitor
 * away to a contact page and stored NOTHING. Every drop we have ever done leaked
 * its whole audience through that hole (the keynote most expensively). WEB-2
 * kills the redirect: this form stays on the page, posts by fetch, and starts a
 * real double opt-in.
 *
 * DESIGN CONTRACT (agents.md §4): frosted glass over UNDIMMED imagery — never a
 * scrim; rem only; gold is scarce; Playfair for the line, Inter for the rest.
 * All of that lives in app/scss/egt.capture.scss; this file only builds nodes.
 *
 * ACCESSIBILITY: a real <form> (so Enter submits natively and password managers
 * behave), a real <label> bound to a real <input id>, an aria-live status
 * paragraph so the inline result is announced, and a honeypot that is
 * unreachable by keyboard (tabindex -1 + aria-hidden) so it can only ever be
 * filled by something automated.
 *
 * NO `action="/contact-us"`. The form deliberately carries NO action/method:
 * if JS ever fails to load, the worst case is a harmless reload of the same
 * page — never a navigation away that swallows the address.
 */

/**
 * SOURCE → NEWSLETTER (segment). The single source of truth, shared by the
 * elements that render a capture and by app/routes/egt.email.capture.js which
 * honours it server-side.
 *
 * FAIL CLOSED: the server treats this as an ALLOWLIST. A `source` the browser
 * makes up is not trusted and cannot select a segment — unknown sources fall
 * back to DEFAULT_SOURCE. That is what stops a hand-crafted POST from adding
 * anyone to an internal list such as `test`.
 */
/* ONE generic newsletter (founder decision 2026-07-29 — supersedes the per-product
 * segmentation of F22/F23 and the 2026-07-29 studio-segment ruling). Too many
 * segments fragment a list that is mostly empty and make it hard to reach anyone;
 * one generic list lets EGT email everyone who signed up for information. So EVERY
 * capture source — every marketing surface AND every waitlist (studio included:
 * joining the waitlist IS joining the newsletter) — funnels into `general`. The
 * per-product slugs (platform/studio/forge/exploit) remain VALID known segments for
 * historical membership + transactional use, but are no longer public newsletters. */
var SOURCES = {
  "home":         { segment: "general" },
  "footer":       { segment: "general" },
  "voice":        { segment: "general" },
  "platform":     { segment: "general" },
  "intrepid":     { segment: "general" },
  "studio":       { segment: "general" },
  "exploit":      { segment: "general" },
  "free-chapter": { segment: "general" },
  "album":        { segment: "general" },
  "forge":        { segment: "general" },
  /* Instagram / social bio-link surfaces (/ig hub + /ig/ginger bridge). Their
   * whole pitch is the free opening of Exploit, so like "free-chapter" they land
   * in the ONE `general` newsletter AND are added to PROMISED_SOURCES in
   * egt.free-sample.js so the book sample is still minted. Distinct source
   * literals so IG signups are ATTRIBUTED on the founder's dashboard (where the
   * audience is coming from) without fragmenting the list. */
  "ig":           { segment: "general" },
  "ig-swole":     { segment: "general" },
  /* The Classic Man Podcast landing (/classicman). A distinct source literal so
   * podcast signups are ATTRIBUTED on the founder's dashboard, but — like every
   * other door — it funnels into the ONE `general` newsletter. NOT added to
   * egt.free-sample.js PROMISED_SOURCES: this is a plain newsletter signup, NOT
   * the Exploit free-sample delivery. The /classicman door renders a required
   * consent tick (consent:true), so it is intentionally NOT consent-exempt. */
  "classicman":   { segment: "general" },
  /* The short-film page (/entertainment/the-journey-of-the-coin) — "more to
   * come" newsletter door. Distinct literal for attribution only; ONE `general`
   * list; NOT a free-sample door (not in PROMISED_SOURCES); renders the required
   * consent tick (consent:true), so NOT consent-exempt. */
  "film":         { segment: "general" },
  /* Create_ podcast pages (/podcasts/create and its episodes) and The Making
   * (/journal). Attribution only; ONE `general` list; both render the required
   * consent tick, so NOT consent-exempt (spec 35). */
  "podcast":      { segment: "general" },
  "journal":      { segment: "general" }
};

var DEFAULT_SOURCE = "footer";

/**
 * DOORS THAT RENDER NO CONSENT TICK — the ONLY exemption to the server-side
 * consent gate among the PUBLIC captures (app/routes/egt.email.capture.js).
 *
 * FOUNDER RULING F33 (2026-07-24): the FOOTER capture ships with NO consent
 * checkbox ("no one else does that in their footer — we can continue to do it
 * everywhere else"). Its consent artefact is the DOUBLE OPT-IN, not a tick, so
 * its captures legitimately arrive with consent:false and must NOT be rejected.
 *
 * Every OTHER public door renders a REQUIRED, never-pre-checked tick (each call
 * site passes consent:true — home, platform, intrepid, voice, album,
 * free-chapter). For those doors the server MUST reject a capture that arrives
 * WITHOUT an affirmative consent:true, or a scripted POST silently bypasses the
 * tick the form made mandatory (F22/F23 finding; founder ruling 2026-07-25 to
 * add the gate).
 *
 * FAIL CLOSED — this is an ALLOWLIST OF EXEMPTIONS, never of requirements. A
 * source that is not listed here REQUIRES consent by default, so a NEW door has
 * to be deliberately added here to skip the gate. And an UNKNOWN source (which
 * resolveSource collapses to DEFAULT_SOURCE == "footer") is NOT trusted to
 * inherit the footer's exemption — see requiresConsent().
 */
var CONSENT_EXEMPT_SOURCES = { footer: true };

/**
 * Does a PUBLIC capture through this resolved source require an affirmative
 * consent:true server-side? Pass the object returned by resolveSource().
 *
 * True for every door except a KNOWN, explicitly tickless source (the footer).
 * A source the browser MADE UP resolves to the footer by fallback but reports
 * `known:false` — it is NOT granted the exemption, so it still requires consent.
 * That closes the "garbage source → footer → tickless" bypass while leaving the
 * real footer (known:true) working exactly as F33 requires.
 *
 * NOTE: this gate belongs on the PUBLIC ROUTE only. The authenticated dashboard
 * adds subscribers/segments by calling the model (subs.upsert / saveSubFile)
 * DIRECTLY — it never passes through the route — so it is structurally exempt
 * and an admin can still add an address with no consent tick.
 *
 * @param {{source:string, segment:string, known:boolean}} mapped
 * @returns {boolean}
 */
function requiresConsent(mapped) {
  mapped = mapped || {};
  if (!mapped.known) { return true; }
  return !Object.prototype.hasOwnProperty.call(CONSENT_EXEMPT_SOURCES, mapped.source);
}

/**
 * "How did you first hear about EGT?" — the attribution question.
 *
 * WHY A QUESTION AND NOT A PIXEL: at this list size self-reported attribution
 * beats any tracking stack. GA4 and the pixel cannot see a person who heard a
 * song on Spotify, remembered the name days later and typed egt.studio into a
 * browser — which is precisely the path the albums are built to create. This
 * one field is the only instrument that can tell us whether the music is
 * working, so it is the only thing standing between "1,000 streams" and
 * knowing whether those streams did anything.
 *
 * WHERE IT IS ASKED (founder ruling 2026-07-22, Q14): NOT on the inline form.
 * The inline form takes the email and the consent tick and nothing else. The
 * question is asked afterwards, in the post-submit dialog that tells the reader
 * to go confirm their address — so it can never stand between a person and the
 * capture, because by the time they see it the capture has already happened.
 *
 * WHY IT IS OPTIONAL:
 *  Optional, and never blocking: the email is the asset. A required question
 *  trades captured addresses for cleaner data, which is exactly backwards
 *  while the list is this small. Dismissing the dialog unanswered is a normal,
 *  fully successful capture. There is deliberately NO "prefer not to say"
 *  option — an unanswered question already says that, and offering it as a
 *  choice only invites people to pick it.
 *
 * "OTHER" CARRIES FREE TEXT (founder ruling 2026-07-22, Q14). That is a real
 * change of contract: this list used to be a closed pick-list precisely so no
 * free text reached storage. The text is therefore treated as hostile — it is
 * accepted ONLY alongside heardAbout === "other", stripped of control
 * characters and hard-capped by sanitizeHeardOther() below, and it is never a
 * segment selector, never a link, and never rendered as HTML.
 *
 * ALLOWLIST, same fail-closed contract as SOURCES: a value the browser makes
 * up is discarded and stored as "" rather than trusted.
 */
var HEARD_ABOUT = [
  { value: "music",     label: "Music — Spotify, Apple, YouTube Music" },
  { value: "youtube",   label: "YouTube" },
  { value: "instagram", label: "Instagram" },
  { value: "tiktok",    label: "TikTok" },
  { value: "friend",    label: "Someone told me" },
  { value: "other",     label: "Other" }
];

/** Hard cap on the "Other" free-text answer. Long enough for a real answer
 * ("a podcast called …"), far too short to smuggle a payload through. */
var HEARD_OTHER_MAX = 120;

var HEARD_ABOUT_VALUES = HEARD_ABOUT.reduce(function (acc, o) {
  acc[o.value] = true; return acc;
}, {});

/**
 * Resolve a (possibly hostile) heard-about literal. Unknown or absent → "",
 * which every consumer reads as "they didn't answer".
 * @param {string} value
 * @returns {string} An allowlisted value, or "".
 */
function resolveHeardAbout(value) {
  var key = (typeof value === "string" ? value : "").trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(HEARD_ABOUT_VALUES, key) ? key : "";
}

/**
 * Sanitize the "Other" free-text answer. The ONLY free text this door accepts,
 * so it is treated as hostile input rather than as a string:
 *  - anything that is not a string, or an unusable one, becomes "";
 *  - C0/C1 control characters (including newlines, which would let one answer
 *    forge extra lines in any plain-text export) are stripped, not escaped;
 *  - whitespace runs collapse, so an answer cannot be padded out to the cap;
 *  - the result is hard-capped at HEARD_OTHER_MAX characters.
 * Callers must additionally refuse it unless heardAbout resolved to "other".
 * @param {string} value
 * @returns {string} A safe, short answer, or "".
 */
function sanitizeHeardOther(value) {
  if (typeof value !== "string") { return ""; }
  /* eslint-disable-next-line no-control-regex */
  var clean = value.replace(/[\u0000-\u001F\u007F-\u009F]+/g, " ").replace(/\s+/g, " ").trim();
  return clean.slice(0, HEARD_OTHER_MAX);
}

/**
 * Resolve a (possibly hostile) source literal to its trusted mapping.
 * @param {string} source
 * @returns {{source:string, segment:string, known:boolean}}
 */
function resolveSource(source) {
  var key = (typeof source === "string" ? source : "").trim().toLowerCase();
  if (Object.prototype.hasOwnProperty.call(SOURCES, key)) {
    return { source: key, segment: SOURCES[key].segment, known: true };
  }
  return { source: DEFAULT_SOURCE, segment: SOURCES[DEFAULT_SOURCE].segment, known: false };
}

/**
 * The honeypot. Named `website` because that is the field name form-filling
 * bots reach for hardest, and left visually off-canvas rather than
 * `display:none` (a class of bot skips display:none fields but happily fills an
 * off-screen one). Real people can neither see it nor tab to it, so ANY value
 * arriving in it is conclusively automated — the server then returns the normal
 * success body and writes nothing, so the bot learns nothing from the attempt.
 */
function honeypot(id) {
  return {
    n: "div", c: "egt-cap__hp", "aria-hidden": "true",
    i: [
      { n: "label", for: id, i: "Leave this field empty" },
      { n: "input", id: id, type: "text", name: "website", value: "",
        tabindex: "-1", autocomplete: "off" }
    ]
  };
}

/**
 * Build a capture block.
 *
 * @param {object} opts
 * @param {string} opts.source     Placement literal — MUST be a key of SOURCES.
 * @param {string} [opts.id]       DOM id prefix (unique per placement on a page).
 * @param {string} [opts.variant]  "panel" (framed frosted card) | "inline"
 *                                 (compact, for the footer). Default "panel".
 * @param {string} [opts.eyebrow]  Small caps line above the title.
 * @param {*}      [opts.title]    Heading content (string or UA/JSON nodes).
 * @param {string} [opts.lead]     One line of copy under the heading.
 * @param {string} [opts.label]    Field label (always rendered, visually hidden
 *                                 in the inline variant).
 * @param {string} [opts.cta]      Button text. Default "Notify me".
 * @param {string} [opts.note]     Fine print under the field.
 * @param {string} [opts.heading]  Heading tag for the title. Default "h2".
 * @returns {object} A UA/JSON node.
 */
function capture(opts) {
  opts = opts || {};

  var mapped = resolveSource(opts.source);
  var variant = opts.variant === "inline" ? "inline" : "panel";
  var idBase = (typeof opts.id === "string" && opts.id) ? opts.id : ("egt-cap-" + mapped.source);
  var emailId = idBase + "-email";
  var noteId = idBase + "-note";
  var cta = (typeof opts.cta === "string" && opts.cta) ? opts.cta : "Notify me";
  var labelText = (typeof opts.label === "string" && opts.label) ? opts.label : "Be first to know.";

  var head = [];
  if (opts.eyebrow) { head.push({ n: "p", c: "egt__eyebrow", i: opts.eyebrow }); }
  if (opts.title) {
    head.push({ n: (opts.heading || "h2"), c: "egt-cap__title", i: opts.title });
  }
  if (opts.lead) { head.push({ n: "p", c: "egt-cap__lead", i: opts.lead }); }

  var field = {
    n: "div", c: "egt-cap__row",
    i: [
      { n: "label", c: "egt-cap__label", for: emailId, i: labelText },
      { n: "div", c: "egt-cap__field",
        i: [
          { n: "input", c: "egt-cap__input", id: emailId, type: "email", name: "email",
            required: true, autocomplete: "email", inputmode: "email",
            spellcheck: "false", placeholder: "you@example.com",
            "aria-describedby": noteId },
          { n: "button", type: "submit", c: "egt-cap__btn",
            "data-egt-capture-submit": "true",
            i: [
              { n: "span", c: "egt-cap__btn-label", i: cta },
              /* the tick doubles as the busy indicator (CSS swaps it for a
               * spinner on .is-busy) so the button never changes width */
              { n: "span", c: "egt-cap__btn-tick", "aria-hidden": "true", i: "›" }
            ] }
        ] }
    ]
  };

  /* ---- the consent tick (Q15, founder ruling 2026-07-22) -----------------
   *
   * REQUIRED, and NEVER pre-checked. The founder asked whether it could ship
   * ticked; the answer is no, and the reason is not stylistic:
   *  - GDPR recital 32 names pre-ticked boxes as an example of what does NOT
   *    constitute consent — consent must be an affirmative ACT;
   *  - double opt-in does not cure a pre-tick. Clicking the link in the
   *    confirmation mail proves the ADDRESS is real and reachable. It does not
   *    prove the person chose to subscribe, because they were never asked;
   *  - and there is nothing to lose here. The free opening of Exploit is the
   *    exchange, so the person ticking the box is being handed something for
   *    it. Required-but-unchecked costs a tap and buys a defensible record.
   * So: no `checked` attribute, ever. The client refuses to submit until it is
   * ticked, and the browser's own `required` is the belt to that braces.
   *
   * OPT-IN PER PLACEMENT (opts.consent), and deliberately NOT a panel default.
   * Q15 was ruled for the /exploit free-chapters door, and a sibling surface
   * has an explicit, opposite ruling already on file: the /voice capture
   * (app/elements/egt.voice.js) records that its required consent checkbox was
   * REMOVED on purpose, on the argument that the confirmation click is the
   * stronger record. Defaulting this on would silently overturn that decision
   * on surfaces nobody asked about. Turning it on elsewhere is a founder call,
   * not a side effect of this one. */
  var wantsConsent = opts.consent === true;
  var consentId = idBase + "-consent";
  var consentNode = wantsConsent ? {
    n: "div", c: "egt-cap__consent",
    i: [
      { n: "input", c: "egt-cap__consent-box", id: consentId, type: "checkbox",
        name: "consent", value: "yes", required: true,
        "data-egt-capture-consent": "true" },
      { n: "label", c: "egt-cap__consent-label", for: consentId,
        i: opts.consentLabel ||
          "Yes — email me news from the studio: what we release, what we build, and what comes next. I can unsubscribe from any email." }
    ]
  } : null;

  /* STEP 2 — the typed code (founder 2026-10-24: every sign-up confirms with a
   * code, never an emailed link). Hidden until the code is sent; the client
   * swaps the email row for this one (.is-code) and back ("use a different
   * email"). Same field + button shape as the email row, so it reads as the
   * same box asking the next question. */
  var codeId = idBase + "-code";
  var codeRow = {
    n: "div", c: "egt-cap__row egt-cap__row--code", hidden: "hidden", "data-egt-capture-code-row": "true",
    i: [
      { n: "label", c: "egt-cap__label egt-cap__label--code", for: codeId,
        i: [ "Enter the code we emailed to ", { n: "span", c: "egt-cap__sent-to", "data-egt-capture-sent-to": "true", i: "you" } ] },
      { n: "div", c: "egt-cap__field",
        i: [
          { n: "input", c: "egt-cap__input egt-cap__input--code", id: codeId, type: "text", name: "code",
            inputmode: "numeric", autocomplete: "one-time-code", spellcheck: "false",
            placeholder: "123 456", maxlength: "16", "aria-describedby": noteId,
            "data-egt-capture-code": "true" },
          { n: "button", type: "submit", c: "egt-cap__btn", "data-egt-capture-verify": "true",
            i: [ { n: "span", c: "egt-cap__btn-label", i: "Confirm" },
              { n: "span", c: "egt-cap__btn-tick", "aria-hidden": "true", i: "›" } ] }
        ] },
      { n: "p", c: "egt-cap__code-help",
        i: [ "Not there? Check Spam or Promotions for a message from ",
          { n: "strong", i: "hello@egtuniverse.com" }, ". ",
          { n: "button", type: "button", c: "egt-cap__linkbtn", "data-egt-capture-resend": "true", i: "Send a new code" },
          " · ",
          { n: "button", type: "button", c: "egt-cap__linkbtn", "data-egt-capture-change": "true", i: "Use a different email" } ] }
    ]
  };

  var body = head.concat([
    field,
    codeRow,
    consentNode,
    honeypot(idBase + "-website"),
    /* role=status + aria-live: the inline result is the ONLY feedback (we never
     * navigate), so it has to reach a screen reader without a focus move. */
    { n: "p", c: "egt-cap__note", id: noteId, "data-egt-capture-note": "true",
      role: "status", "aria-live": "polite", i: "" },
    { n: "p", c: "egt-cap__fine",
      i: [
        (typeof opts.note === "string" && opts.note)
          ? opts.note
          : "Letters about building EGT, the films and the Universe. We'll email you a code to confirm. Unsubscribe anytime — see the ",
        /* H17 — `#privacy` was a dead fragment: no such id has ever existed in
         * the rendered Codex. The real target is Part 3, whose id is emitted by
         * app/helpers/egt.markdown.js `headingId()` (a fixed `sec-` prefix over
         * the slugged heading). This link therefore lives OFF the surface it
         * points into: any change to heading-id generation must update it here
         * as well — the same trap that broke the /legal hub's Privacy card. */
        { n: "a", href: "/legal/codex#sec-part-3-privacy-and-data", c: "egt-underline", i: "EGT Codex" }, "."
      ] }
  /* filter(Boolean) drops askNode when the placement opts out. A literal null
   * left in a children array is not inert in UA/JSON — strip it. */
  ]).filter(Boolean);

  var form = {
    n: "form", c: "egt-cap__form", id: idBase + "-form",
    /* NO action / NO method — see the file header. JS owns the submit. */
    novalidate: "novalidate",
    "data-egt-capture": "true",
    "data-egt-capture-source": mapped.source,
    i: body
  };

  return {
    n: "div", c: "egt-cap egt-cap--" + variant, id: idBase,
    i: [
      (variant === "panel")
        ? { n: "div", c: "egt-glass egt-cap__card", i: form }
        : form,
      confirmDialog(idBase, opts, variant)
    ].filter(Boolean)
  };
}

/**
 * THE POST-SUBMIT DIALOG (Q14, founder ruling 2026-07-22).
 *
 * Rendered with the capture, closed, and opened by the client once the typed
 * code is CONFIRMED (2026-10-24; it used to open after the email step and tell
 * the reader to click a link). It welcomes them, then asks how they first
 * heard about EGT.
 *
 * The question is a BONUS and is never load-bearing. The subscription is
 * already confirmed (the code was accepted) before this opens, so the
 * close button, Escape, a backdrop click and "Skip" are all fully successful
 * outcomes. Nothing here can undo a capture.
 *
 * WHY A REAL <dialog> AND NOT A DIV: `showModal()` puts it in the browser's TOP
 * LAYER, which escapes every `overflow:hidden`, `transform` and `filter`
 * ancestor on these pages (G-1/G-12 territory — `.egt-canvas` and the parallax
 * media layers would otherwise clip or mis-position a fixed div). It also gives
 * us the focus trap, Escape, and inert background for free. And unlike
 * `ua.dialog` it is NOT portaled out to <body> (G-7): the node stays inside
 * `.egt`, so every `var(--egt-*)` brand token still resolves.
 *
 * OPT-IN PER PLACEMENT (opts.dialog), for the same reason as the consent tick:
 * Q14 was ruled for the /exploit door, and throwing a modal over every other
 * surface's capture is a decision those surfaces' owners have not made. It is
 * also the wrong shape for the compact inline footer strip, where a modal
 * raised because somebody tapped a one-line field is an interruption rather
 * than a service.
 *
 * @param {string} idBase
 * @param {object} opts    Same options object as capture().
 * @param {string} variant "panel" | "inline". Reserved for per-variant styling.
 * @returns {object|null} A UA/JSON node, or null when the placement opts out.
 */
function confirmDialog(idBase, opts, variant) {
  if (opts.dialog !== true) { return null; }

  var titleId = idBase + "-dialog-title";
  var askId = idBase + "-heard";
  var otherId = idBase + "-heard-other";

  /* The "check your spam" hint lives in the code step now (the code row above),
   * where it is needed: this dialog opens only after the code is confirmed. */

  var ask = {
    n: "div", c: "egt-cap__ask",
    i: [
      { n: "label", c: "egt-cap__ask-label", for: askId,
        i: (typeof opts.askLabel === "string" && opts.askLabel)
          ? opts.askLabel
          : "While you're here — how did you first hear about EGT?" },
      /* NO "prefer not to say" option (founder, Q14). The first option is an
       * empty PLACEHOLDER, not an answer: leaving it selected and closing the
       * dialog is the same thing as never opening it. */
      { n: "select", c: "egt-cap__ask-select", id: askId, name: "heardAbout",
        "data-egt-capture-heard": "true",
        i: [{ n: "option", value: "", i: "Choose one…" }].concat(
          HEARD_ABOUT.map(function (o) {
            return { n: "option", value: o.value, i: o.label };
          })
        ) },
      /* "Other" opens a free-text line. Hidden (and, importantly, DISABLED, so
       * it is skipped by the tab order and never submitted) until "Other" is
       * the selection — the client toggles both. */
      { n: "div", c: "egt-cap__ask-other", "data-egt-capture-other-wrap": "true", hidden: "hidden",
        i: [
          { n: "label", c: "egt-cap__ask-label", for: otherId, i: "Where was that?" },
          { n: "input", c: "egt-cap__ask-input", id: otherId, type: "text",
            name: "heardAboutOther", maxlength: String(HEARD_OTHER_MAX),
            autocomplete: "off", disabled: "disabled",
            placeholder: "A podcast, a bookstore, a friend's playlist…",
            "data-egt-capture-other": "true" }
        ] }
    ]
  };

  return {
    n: "dialog", c: "egt-cap__dialog", id: idBase + "-dialog",
    "data-egt-capture-dialog": "true", "aria-labelledby": titleId,
    i: { n: "div", c: "egt-cap__dialog-box",
      i: [
        { n: "button", type: "button", c: "egt-cap__dialog-close",
          "data-egt-capture-dialog-close": "true", "aria-label": "Close",
          i: { n: "span", "aria-hidden": "true", i: "×" } },
        { n: "p", c: "egt-cap__dialog-eyebrow",
          i: (typeof opts.dialogEyebrow === "string" && opts.dialogEyebrow)
            ? opts.dialogEyebrow : "Welcome" },
        { n: "h2", c: "egt-cap__dialog-title", id: titleId,
          i: (typeof opts.dialogTitle === "string" && opts.dialogTitle)
            ? opts.dialogTitle : "You're in." },
        { n: "p", c: "egt-cap__dialog-lead",
          i: (typeof opts.dialogLead === "string" && opts.dialogLead)
            ? opts.dialogLead
            : "Your address is confirmed. You'll get letters about building EGT, the films and the Universe." },
        ask,
        { n: "div", c: "egt-cap__dialog-actions",
          i: [
            { n: "button", type: "button", c: "egt-cap__dialog-save",
              "data-egt-capture-heard-save": "true", i: "Send answer" },
            { n: "button", type: "button", c: "egt-cap__dialog-skip",
              "data-egt-capture-dialog-close": "true", i: "Skip this" }
          ] },
        { n: "p", c: "egt-cap__dialog-note", "data-egt-capture-dialog-note": "true",
          role: "status", "aria-live": "polite", i: "" }
      ] }
  };
}

/**
 * The client module that drives every capture on the page. Emitted from the
 * shared footer (app/helpers/egt.sections.js) so a capture works on EVERY
 * surface without each page's JSON having to remember to add the script.
 * @returns {object} A UA/JSON <script> node.
 */
function captureScript() {
  return { n: "script", src: "/src/egt.capture.js", type: "text/javascript", defer: "defer" };
}

module.exports = {
  capture: capture,
  captureScript: captureScript,
  resolveSource: resolveSource,
  requiresConsent: requiresConsent,
  CONSENT_EXEMPT_SOURCES: CONSENT_EXEMPT_SOURCES,
  resolveHeardAbout: resolveHeardAbout,
  sanitizeHeardOther: sanitizeHeardOther,
  SOURCES: SOURCES,
  DEFAULT_SOURCE: DEFAULT_SOURCE,
  HEARD_ABOUT: HEARD_ABOUT,
  HEARD_OTHER_MAX: HEARD_OTHER_MAX
};
